// prettier-ignore
/** Searchspring header dropdown helpers (autocomplete + suggest). */
let _searchspringPageLoadId;
function searchspringDebugEnabled() {
	return (
		window.searchspringDebug === true ||
		new URLSearchParams(window.location.search).get("ss_debug") === "1"
	);
}
function searchspringLog(label, data) {
	if (!searchspringDebugEnabled()) return;
	try {
		console.groupCollapsed(`[Searchspring] ${label}`);
		console.log("parsed:", data);
		console.log("json:", JSON.stringify(data, null, 2));
		console.groupEnd();
	} catch (e) {
		console.warn("[Searchspring] log failed", e);
	}
}
function randomUuidV4() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}
function getCookie(name) {
	const safe = String(name).replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
	const m = document.cookie.match(new RegExp(`(?:^|; )${safe}=([^;]*)`));
	return m ? decodeURIComponent(m[1]) : "";
}
function setCookie(name, value, days = 365) {
	const d = new Date();
	d.setTime(d.getTime() + days * 864e5);
	document.cookie = `${name}=${encodeURIComponent(value)};path=/;expires=${d.toUTCString()};SameSite=Lax`;
}
function getSearchspringBrowserContext() {
	let userId = getCookie("ssUserId");
	if (!userId) {
		userId =
			typeof crypto !== "undefined" && crypto.randomUUID
				? crypto.randomUUID()
				: randomUuidV4();
		setCookie("ssUserId", userId);
	}
	let sessionId = sessionStorage.getItem("ssSessionId");
	if (!sessionId) {
		sessionId =
			typeof crypto !== "undefined" && crypto.randomUUID
				? crypto.randomUUID()
				: randomUuidV4();
		sessionStorage.setItem("ssSessionId", sessionId);
	}
	if (!_searchspringPageLoadId) {
		_searchspringPageLoadId =
			typeof crypto !== "undefined" && crypto.randomUUID
				? crypto.randomUUID()
				: randomUuidV4();
	}
	return {
		userId,
		sessionId,
		pageLoadId: _searchspringPageLoadId,
		domain: window.location.hostname || "",
	};
}
function buildSearchspringAutocompleteParams(siteId, opts) {
	const ctx = getSearchspringBrowserContext();
	const p = new URLSearchParams();
	p.set("siteId", siteId);
	p.set("resultsFormat", "json");
	p.set("userId", ctx.userId);
	p.set("sessionId", ctx.sessionId);
	p.set("pageLoadId", ctx.pageLoadId);
	p.set("domain", ctx.domain);
	p.set("redirectResponse", "full");
	p.set("resultsPerPage", String(opts.resultsPerPage ?? 10));
	p.set("page", String(opts.page ?? 1));
	const q = opts.q;
	if (q != null && String(q).length) {
		p.set("q", String(q));
		p.set("input", String(opts.input ?? q));
	}
	if (opts.includedFacets) p.set("includedFacets", opts.includedFacets);
	return p;
}
function fetchSearchspringAutocomplete(siteId, params, signal) {
	const url = `https://${siteId}.a.searchspring.io/api/search/autocomplete.json?${params.toString()}`;
	return fetch(url, { signal, headers: { Accept: "application/json" } }).then(
		async (r) => {
			const text = await r.text();
			let json = {};
			try {
				json = text ? JSON.parse(text) : {};
			} catch {
				json = { _parseError: true, _raw: text };
			}
			if (!r.ok) {
				searchspringLog(`search.json HTTP ${r.status}`, json);
				throw new Error(`Searchspring search ${r.status}`);
			}
			return json;
		},
	);
}
function fetchSearchspringSuggest(siteId, q, signal) {
	const p = new URLSearchParams({
		siteId,
		suggestionCount: "10",
		productCount: "0",
	});
	if (q != null && String(q).length) p.set("q", String(q));
	const url = `https://${siteId}.a.searchspring.io/api/suggest/query?${p.toString()}`;
	return fetch(url, { signal, headers: { Accept: "application/json" } })
		.then(async (r) => {
			const text = await r.text();
			let json = {};
			try {
				json = text ? JSON.parse(text) : {};
			} catch {
				json = { _parseError: true, _raw: text };
			}
			if (!r.ok) {
				searchspringLog(`suggest/query HTTP ${r.status}`, json);
				return {};
			}
			return json;
		})
		.catch(() => ({}));
}
function fetchSearchspringTrending(siteId, limit, signal) {
	const p = new URLSearchParams({
		siteId,
		limit: String(limit ?? 10),
	});
	const url = `https://${siteId}.a.searchspring.io/api/suggest/trending?${p.toString()}`;
	return fetch(url, { signal, headers: { Accept: "application/json" } })
		.then(async (r) => {
			const text = await r.text();
			let json = {};
			try {
				json = text ? JSON.parse(text) : {};
			} catch {
				json = { _parseError: true, _raw: text };
			}
			if (!r.ok) {
				searchspringLog(`suggest/trending HTTP ${r.status}`, json);
				return {};
			}
			return json;
		})
		.catch(() => ({}));
}
function extractTrendingTerms(trendingData) {
	const arr =
		trendingData?.trending?.queries ||
		trendingData?.queries ||
		trendingData?.data?.queries ||
		[];
	return arr
		.map((x) =>
			String(x?.searchQuery ?? x?.query ?? x?.text ?? "").trim(),
		)
		.filter(Boolean)
		.slice(0, 12);
}
function extractRelatedSearchTerms(suggestData) {
	const terms = [];
	const seen = new Set();
	const add = (t) => {
		const s = String(t || "").trim();
		if (!s || seen.has(s.toLowerCase())) return;
		seen.add(s.toLowerCase());
		terms.push(s);
	};
	if (suggestData?.suggested?.text) add(suggestData.suggested.text);
	(suggestData?.alternatives || []).forEach((a) => add(a.text));
	return terms.slice(0, 10);
}
function facetScore(f) {
	const field = String(f?.field || f?.name || f?.label || "").toLowerCase();
	let s = 0;
	if (field.includes("collection")) s += 12;
	if (field.includes("category") || field.includes("hierarchy")) s += 10;
	if (field.includes("product_type") || field.includes("type")) s += 6;
	if (field.includes("vendor")) s += 4;
	if (field.includes("ss_") || field.includes("sson")) s += 3;
	return s;
}
function extractCollectionLinksFromFacets(facets) {
	if (!Array.isArray(facets) || !facets.length) return [];
	const sorted = [...facets].sort((a, b) => facetScore(b) - facetScore(a));
	const best = sorted[0];
	if (!best || !Array.isArray(best.values)) return [];
	return best.values
		.slice(0, 8)
		.map((v) => ({
			label: String(v.label ?? v.value ?? v.filterValue ?? "").trim(),
			filterValue: v.filterValue ?? v.value,
		}))
		.filter((x) => x.label);
}
function autocompleteTotalResults(data) {
	return (
		data?.pagination?.totalResults ??
		data?.meta?.totalResults ??
		data?.meta?.total ??
		(Array.isArray(data?.results) ? data.results.length : 0)
	);
}
function escapeHtmlAttr(unsafe) {
	return String(unsafe ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
}
function highlightQueryHtml(text, query) {
	const t = String(text ?? "");
	if (!query || !String(query).trim()) return escapeHtmlAttr(t);
	const q = String(query);
	let re;
	try {
		re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
	} catch {
		return escapeHtmlAttr(t);
	}
	const out = [];
	let last = 0;
	let m;
	while ((m = re.exec(t)) !== null) {
		out.push(escapeHtmlAttr(t.slice(last, m.index)));
		out.push(`<mark>${escapeHtmlAttr(m[0])}</mark>`);
		last = m.index + m[0].length;
		if (m[0].length === 0) re.lastIndex++;
	}
	out.push(escapeHtmlAttr(t.slice(last)));
	return out.join("");
}
function getProductCollectionLabel(p) {
	if (!p || typeof p !== "object") return "";
	if (p.brand) return String(p.brand).trim();
	if (p.collection_title) return String(p.collection_title);
	if (p.collectionTitle) return String(p.collectionTitle);
	if (p.collection) return String(p.collection);
	if (Array.isArray(p.collections) && p.collections.length) {
		const c = p.collections[0];
		return String(c?.title || c?.name || c || "");
	}
	if (p.ss_category) return String(p.ss_category);
	if (p.category_hierarchy) {
		const parts = String(p.category_hierarchy)
			.split(/[>|/]/)
			.map((s) => s.trim())
			.filter(Boolean);
		return parts[parts.length - 1] || "";
	}
	if (p.product_type) return String(p.product_type);
	if (p.vendor) return String(p.vendor);
	return "";
}
function categoryClassesForProduct(p, fallbackClassString) {
	const handles = [];
	if (Array.isArray(p?.collections)) {
		for (const c of p.collections) {
			const h =
				c?.handle ||
				(typeof c?.url === "string" &&
					c.url.match(/collections\/([^/?]+)/)?.[1]);
			if (h) handles.push(`category-${h}`);
		}
	}
	if (handles.length) return handles.join(" ");
	return fallbackClassString;
}

function parseSsMoney(value) {
	if (value === null || value === undefined) return 0;
	const raw =
		typeof value === "number"
			? value
			: parseFloat(String(value).replace(/[^0-9.-]/g, ""));
	return Number.isFinite(raw) ? raw : 0;
}
// value must already be in cents. Applies window.theme.applyDisplayTax if enabled.
function formatSsMoney(value, tags) {
	let cents = Math.round(parseSsMoney(value));
	if (typeof window.theme?.applyDisplayTax === "function") {
		cents = window.theme.applyDisplayTax(cents, Array.isArray(tags) ? tags : []);
	}
	if (typeof window.formatMoney === "function") return window.formatMoney(cents);
	return `$${(cents / 100).toFixed(2)}`;
}
function ssProductInStock(p) {
	if (p?.available_for_sale !== undefined && p?.available_for_sale !== null) {
		return !!p.available_for_sale;
	}
	if (p?.ss_available !== undefined && p?.ss_available !== null) {
		const v = String(p.ss_available).toLowerCase();
		return v === "true" || v === "1";
	}
	if (p?.stockMessage) {
		return !String(p.stockMessage).toLowerCase().includes("out");
	}
	return true;
}
function buildSsDropdownCard(p) {
	const str = window.themeSearchStrings || {};
	const esc = escapeHtmlAttr;
	const title = p?.title || p?.name || "";
	const handle =
		p?.handle ||
		(p?.url
			? String(p.url).replace(/^\/products\//, "").replace(/\/$/, "")
			: p?.uid);
	let url = p?.url || (handle ? `/products/${handle}` : "#");
	if (typeof url === "string" && url.startsWith("//")) url = `https:${url}`;
	const imageUrl =
		p?.featured_image?.src ||
		p?.imageUrl ||
		p?.thumbnailImageUrl ||
		p?.image?.url ||
		"";
	const available = ssProductInStock(p);
	// Searchspring returns prices in dollars — convert to cents at source.
	const priceCents = Math.round(parseSsMoney(p?.price) * 100);
	const msrpCents = Math.round(parseSsMoney(p?.msrp) * 100);
	const onSale = msrpCents > priceCents && priceCents > 0;
	const pTags = Array.isArray(p?.tags) ? p.tags : [];
	const regularPriceFormatted = formatSsMoney(onSale ? msrpCents : priceCents, pTags);
	const salePriceFormatted = onSale ? formatSsMoney(priceCents, pTags) : "";
	const percentOff = onSale ? Math.floor(((msrpCents - priceCents) / msrpCents) * 100) : 0;
	const prpLabel = str.prp || "RRP";
	const avail = str.available || "Available";
	const soldOut = str.soldOut || "Sold out";
	const qty = parseInt(
		p?.inventory_quantity ?? p?.variant_inventory ?? p?.stock_quantity ?? "",
		10,
	);
	let lowStockCaption = "";
	if (available && !Number.isNaN(qty) && qty > 0 && qty < 10) {
		if (qty === 1) {
			lowStockCaption = str.lastOneItems || "";
		} else if (str.lastItems) {
			lowStockCaption = String(str.lastItems).replace(
				/\{\{\s*quantity\s*\}\}/gi,
				String(qty),
			);
		}
	}
	const badgeHtml =
		onSale && percentOff > 0
			? `<div class="card__badge"><span class="badge badge--sale">-${percentOff}%</span></div>`
			: "";
	const inventoryHtml = "";
	const priceClasses = ["price", !available ? "price--sold-out" : "", onSale ? "price--on-sale" : ""]
		.filter(Boolean)
		.join(" ");
	const priceHtml = onSale
		? `<div class="${priceClasses}">
			<dl>
				<div class="price__regular">
					<dd><span class="price-item price-item--regular">${esc(regularPriceFormatted)}</span></dd>
				</div>
				<div class="price__sale">
					<dd><span class="price-item price-item--sale">${esc(salePriceFormatted)}</span></dd>
					<dd class="price__compare"><s class="price-item price-item--regular">${esc(prpLabel)} ${esc(
				regularPriceFormatted,
			)}</s></dd>
				</div>
			</dl>
		</div>`
		: `<div class="${priceClasses}">
			<dl>
				<div class="price__regular">
					<dd><span class="price-item price-item--regular">${esc(regularPriceFormatted)}</span></dd>
				</div>
			</dl>
		</div>`;
	const collectionLabel = getProductCollectionLabel(p);
	const collectionBlock = collectionLabel
		? `<div class="subtitle card_title_font card__collection-title">${esc(collectionLabel)}</div>`
		: "";
	return `
		<div class="product-card card-wrapper js-animation-card js-color-swatches-wrapper">
			<span class="visually-hidden">${esc(title)}</span>
			<div class="card card--product">
				<div class="card__inner full-unstyled-link">
					<a href="${esc(url)}" class="media media--hover-effect js-color-swatches-link" aria-label="Product link" style="padding-bottom: 100%;">
						${imageUrl ? `<img class="motion-reduce media--first" src="${esc(imageUrl)}" alt="${esc(title)}" loading="lazy" />` : ""}
					</a>
				</div>
				<div class="card__information-top">
					${badgeHtml}
				</div>
				<div class="card-information-box card-content">
					<div class="card-information">
						${collectionBlock}
						<div class="card-information-block">
							<h3 class="card__title">
								<a class="unstyled-link" href="${esc(url)}" title="${esc(title)}">${esc(title)}</a>
							</h3>
							${priceHtml}
						</div>
						${inventoryHtml}
					</div>
				</div>
			</div>
			<a class="product_card_product-link" href="${esc(url)}" aria-hidden="true"></a>
		</div>
	`;
}
function buildSsTypingSidebarHtml(
	relatedTerms,
	collectionLinks,
	searchTerm,
	searchBaseUrl,
	sidebarQueriesHeading,
) {
	const str = window.themeSearchStrings || {};
	const collectionsHeading = str.collections || "Collections";
	const queriesHeading = sidebarQueriesHeading || "Popular Searches";
	const hasRelated = relatedTerms.length > 0;
	const hasCols = collectionLinks.length > 0;
	if (!hasRelated && !hasCols) return "";
	let html = `<div class="predictive-search__sidebar">`;
	if (hasRelated) {
		html += `<div class="predictive-search__sidebar-section"><h3 class="predictive-search__sidebar-heading">${escapeHtmlAttr(queriesHeading)}</h3><ul class="${searchTerm ? "predictive-search__sidebar-list" : "predictive-search-initial-sidebar__list"} list-unstyled">`;
		for (const term of relatedTerms) {
			const href = `${searchBaseUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
			html += `<li class="predictive-search__sidebar-item"><a href="${escapeHtmlAttr(href)}" class="${searchTerm ? "predictive-search__sidebar-link" : "predictive-search-initial-sidebar__link"}">${highlightQueryHtml(term, searchTerm)}</a></li>`;
		}
		html += `</ul></div>`;
	}
	if (hasCols) {
		html += `<div class="predictive-search__sidebar-section"><h3 class="predictive-search__sidebar-heading">${escapeHtmlAttr(collectionsHeading)}</h3><ul class="${searchTerm ? "predictive-search__sidebar-list" : "predictive-search-initial-sidebar__list"} list-unstyled">`;
		for (const c of collectionLinks) {
			const href = `${searchBaseUrl}?q=${encodeURIComponent(c.label)}&options%5Bprefix%5D=last`;
			html += `<li class="predictive-search__sidebar-item"><a href="${escapeHtmlAttr(href)}" class="${searchTerm ? "predictive-search__sidebar-link" : "predictive-search-initial-sidebar__link"}">${highlightQueryHtml(c.label, searchTerm)}</a></li>`;
		}
		html += `</ul></div>`;
	}
	html += `</div>`;
	return html;
}

function buildSsDropdownResultsMarkup({
	searchTerm,
	searchBaseUrl = "/search",
	products,
	totalResults,
	relatedTerms,
	collectionLinks,
	categoryClassString,
	sidebarQueriesHeading,
	relatedProductsHeading,
	liveRegionText,
}) {
	const str = window.themeSearchStrings || {};
	const esc = escapeHtmlAttr;
	const productsTabLabel = str.products || "Products";
	const viewAllHref = searchTerm
		? `${searchBaseUrl}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last`
		: `${searchBaseUrl}?options%5Bprefix%5D=last`;
	const relatedHeading = relatedProductsHeading || "Related Products";

	const sidebarHtml = buildSsTypingSidebarHtml(
		relatedTerms,
		collectionLinks,
		searchTerm,
		searchBaseUrl,
		sidebarQueriesHeading,
	);
	const hasRelated = relatedTerms.length > 0;
	const hasCols = collectionLinks.length > 0;
	const noProducts = products.length === 0;
	const noSidebar = !hasRelated && !hasCols;
	const wrapperClass = [
		"predictive-search__results-groups-wrapper",
		noProducts ? "predictive-search__results-groups-wrapper--no-products" : "",
		noSidebar ? "predictive-search__results-groups-wrapper--no-suggestions" : "",
	]
		.filter(Boolean)
		.join(" ");

	const productsListItems = products
		.map((p, idx) => {
			const cat = categoryClassesForProduct(p, categoryClassString);
			return `
				<li
					id="predictive-search-option-product-${idx + 1}"
					class="predictive-search__list-item collection-product-card swiper-slide ${cat}"
					role="option"
					aria-selected="false"
				>
					${buildSsDropdownCard(p)}
				</li>
			`;
		})
		.join("");

	const noResultsLine = searchTerm
		? `No results for '${esc(searchTerm)}'`
		: esc("No products to show");

	const mainColumn =
		products.length > 0
			? `
							<div class="predictive-search__result-group-header">
								<span class="predictive-search__result-group-heading">${esc(relatedHeading)}</span>
								${searchTerm ? `<a href="${esc(viewAllHref)}" class="predictive-search__view-all button button--primary desktop-only">
										${
											(searchTerm && totalResults === 1)
												? "View 1 Result"
												: `View All ${totalResults} Results`
										}
									</a>` : ''
								}
							</div>
							<div class="predictive-search-results-wrapper color-background-2">
								<div class="predictive-search-slider-container">
									<div class="predictive-search-products-slider swiper" data-type="products">
										<ul
											id="predictive-search-results-products-list"
											class="predictive-search__results-list predictive-search__results-list_products list-unstyled swiper-wrapper"
											role="group"
											aria-labelledby="predictive-search-products"
											tabIndex="-1"
											data-type="products"
										>
											${productsListItems}
										</ul>
									</div>
									<button class="predictive-slider-nav predictive-slider-prev" aria-label="Previous">
										<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon icon-swiper-prev">
											<path d="M11 3L5 9L11 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
										</svg>
									</button>
									<button class="predictive-slider-nav predictive-slider-next" aria-label="Next">
										<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon icon-swiper-next">
											<path d="M7 3L13 9L7 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
										</svg>
									</button>
								</div>
							</div>
							${searchTerm ? `<div class="bottom-search-link mobile-only">
									<a href="${esc(viewAllHref)}" class="predictive-search__view-all button button--primary">
										View All ${totalResults} Results
									</a>
								</div>` : ''
							}`
			: ``;

	return `
					<div id="predictive-search-results-groups-wrapper" class="${esc(wrapperClass)}">
						${sidebarHtml}
						<div class="predictive-search__result-group">
							<div class="predictive-search__result-tabs">
								<div class="predictive-search__result-tab active" data-type-target="products">
									<span class="predictive-search__result-tab-title small-font">${esc(productsTabLabel)}</span>
								</div>
							</div>
							${mainColumn}
						</div>
					</div>
					<span class="hidden" data-predictive-search-live-region-count-value>
						${esc(liveRegionText)}
					</span>
				`;
}

const PREDICTIVE_PRODUCTS_TWO_UP_MQ = "(max-width: 989px)";

function updatePredictiveProductsNextNav(swiper, nextEl) {
	if (!nextEl || !swiper?.slides?.length) return;

	const twoUp = window.matchMedia(PREDICTIVE_PRODUCTS_TWO_UP_MQ).matches;
	const slideCount = swiper.slides.length;
	const atLastPair =
		slideCount <= 2 || swiper.activeIndex >= slideCount - 2;
	const hideNext = twoUp && atLastPair;

	nextEl.classList.toggle("swiper-button-disabled", hideNext);
	nextEl.toggleAttribute("disabled", hideNext);
}

class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.header = document.querySelector(".shopify-section-header");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");

		// Searchspring (powers the header search dropdown).
		// Override via `window.searchspringSiteId` or a `data-searchspring-site-id`
		// attribute on the <predictive-search> element.
		this.searchspringSiteId =
			this.dataset?.searchspringSiteId ||
			window.searchspringSiteId ||
			window?.mrSearchConfig?.searchspringSiteId ||
			"7mtmqg";
		this.searchspringResultsPerPage = parseInt(
			this.dataset?.searchspringResultsPerPage || "10",
			10,
		);
		this.calcHeight();
		this.setupEventListeners();
	}

	calcHeight() {
		this.results = document.querySelector(".predictive-search__result-group");
		this.resultsTabs = document.querySelector(
			".predictive-search__result-tabs",
		);
		if (this.results && this.resultsTabs) {
			this.results.style.setProperty(
				"--search-height",
				`${parseInt(this.resultsTabs.getBoundingClientRect().bottom, 10) + 100}px`,
			);
		}
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));

		// Track interaction timestamp to prevent search closing on focusout
		// Uses capture phase so it fires BEFORE Swiper or any child can stopPropagation
		// Swiper uses pointer events, so we need pointerdown as well
		this._lastInteractionTime = 0;
		const updateInteraction = () => { 
			this._lastInteractionTime = Date.now(); 
		};
		this.addEventListener("mousedown", updateInteraction, true);
		this.addEventListener("touchstart", updateInteraction, true);
		this.addEventListener("pointerdown", updateInteraction, true);
		this.addEventListener("click", updateInteraction, true);
		// Reset timestamp on mouse/pointer up so subsequent outside clicks aren't blocked
		document.addEventListener("pointerup", () => {
			setTimeout(() => { this._lastInteractionTime = 0; }, 200);
		});
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this._searchspringInitialHydrated = false;
			if (this.predictiveSearchResults) {
				this.predictiveSearchResults.innerHTML = "";
			}
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			this.maybeHydrateSearchspringInitial();
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut(event) {
		// Check if focus moved to element inside this component
		if (event.relatedTarget && this.contains(event.relatedTarget)) {
			return;
		}

		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		} else {
			setTimeout(() => {
				// If interaction was recent (slider drag/click inside), refocus input
				const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
				const timeSinceInteraction = Date.now() - this._lastInteractionTime;
				if (timeSinceInteraction < 400) {
					this.input.focus();
					return;
				}

				if (!isIOS && !this.contains(document.activeElement)) {
					this.close(true);
				}
			}, 50);
		}
	}

	onBlur() {
		// this.headerInput.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);
		event.preventDefault();

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				this.close(true);
				this.closeResults(true);
				if (this.getQuery().length) {
					this.hidePromoBlock();
				} else {
					this.showPromoBlock();
				}
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.clickSearchTabs();
			this.initProductsSlider();
			this.calcHeight();
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		const siteId = this.searchspringSiteId;
		const resultsPerPage = Number.isFinite(this.searchspringResultsPerPage)
			? this.searchspringResultsPerPage
			: 10;
		const searchBaseUrl = window.routes?.search_url || "/search";

		const headerSearchEl = this.closest("header-search");
		const categoryHandles = headerSearchEl
			? Array.from(
					headerSearchEl.querySelectorAll(
						".collection_list_items[data-category-handle]",
					),
				)
					.map((el) => el.getAttribute("data-category-handle"))
					.filter(Boolean)
			: ["all"];
		const categoryClassString = categoryHandles.map((h) => `category-${h}`).join(" ");

		const acParams = buildSearchspringAutocompleteParams(siteId, {
			resultsPerPage,
			page: 1,
			q: searchTerm,
			input: searchTerm,
		});

		const autoPromise = fetchSearchspringAutocomplete(
			siteId,
			acParams,
			this.abortController.signal,
		).catch((err) => {
			searchspringLog("search.json error (typing)", { message: err?.message });
			return {};
		});

		Promise.all([
			autoPromise,
			fetchSearchspringSuggest(siteId, searchTerm, this.abortController.signal),
		])
			.then(([autoData, suggestData]) => {
				searchspringLog("search.json (typing)", autoData);
				searchspringLog("suggest/query (typing)", suggestData);

				const products = Array.isArray(autoData?.results) ? autoData.results : [];
				const totalResults = autocompleteTotalResults(autoData);
				const relatedTerms = extractRelatedSearchTerms(suggestData);
				const facets =
					autoData?.facets ||
					autoData?.filters ||
					autoData?.filterSummary ||
					[];
				const collectionLinks = extractCollectionLinksFromFacets(facets);
				const resultsCountText = `${totalResults} Results for "${searchTerm}"`;
				const resultsMarkup = buildSsDropdownResultsMarkup({
					searchTerm,
					searchBaseUrl,
					products,
					totalResults,
					relatedTerms,
					collectionLinks,
					categoryClassString,
					sidebarQueriesHeading: undefined,
					relatedProductsHeading: "Related Products",
					liveRegionText: resultsCountText,
				});

				this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
					predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
				});

				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				this.initProductsSlider();
				this.calcHeight();
			})
			.catch((error) => {
				if (error?.code === 20 || error?.name === "AbortError") {
					return;
				}
				this.close();
				throw error;
			});

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	maybeHydrateSearchspringInitial() {
		if (this.dataset.searchspringHydrateInitial === "false") return;
		// First open: fetch once and set _searchspringInitialHydrated. Closing only removes
		// [open] (see closeResults); markup usually remains. On refocus we must re-open the
		// panel — otherwise maybeHydrate returns early and the dropdown stays display:none.
		if (this._searchspringInitialHydrated) {
			const hasInitialMarkup = this.querySelector(
				"#predictive-search-results-groups-wrapper",
			);
			if (hasInitialMarkup) {
				this.setAttribute("results", true);
				this.setLiveRegionResults();
				this.open();
				this.initProductsSlider();
				this.calcHeight();
			}
			return;
		}
		if (this._searchspringInitialPending) return;
		if (!this.predictiveSearchResults) return;

		this._searchspringInitialPending = true;
		const siteId = this.searchspringSiteId;
		const searchBaseUrl = window.routes?.search_url || "/search";
		const str = window.themeSearchStrings || {};
		const trendingHeading = str.trendingSearches || "Trending searches";
		const trendingProductsHeading = str.trendingProducts || "Trending products";

		const headerSearchEl = this.closest("header-search");
		const categoryHandles = headerSearchEl
			? Array.from(
					headerSearchEl.querySelectorAll(
						".collection_list_items[data-category-handle]",
					),
				)
					.map((el) => el.getAttribute("data-category-handle"))
					.filter(Boolean)
			: ["all"];
		const categoryClassString = categoryHandles.map((h) => `category-${h}`).join(" ");

		const acParams = buildSearchspringAutocompleteParams(siteId, {
			resultsPerPage: 12,
			page: 1,
		});

		Promise.all([
			fetchSearchspringAutocomplete(siteId, acParams, this.abortController.signal).catch(
				() => ({}),
			),
			fetchSearchspringTrending(siteId, 12, this.abortController.signal),
		])
			.then(([autoData, trendingData]) => {
				searchspringLog("search.json (initial / no query)", autoData);
				searchspringLog("suggest/trending (initial)", trendingData);

				const products = Array.isArray(autoData?.results) ? autoData.results : [];
				const totalResults = autocompleteTotalResults(autoData);
				const relatedTerms = extractTrendingTerms(trendingData);
				const collectionLinks = [];
				if (!products.length && !relatedTerms.length) {
					return;
				}

				this._searchspringInitialHydrated = true;

				const liveRegionText =
					products.length > 0
						? `${totalResults} ${trendingProductsHeading}`
						: relatedTerms.length > 0
							? trendingHeading
							: "Search";

				const resultsMarkup = buildSsDropdownResultsMarkup({
					searchTerm: "",
					searchBaseUrl,
					products,
					totalResults,
					relatedTerms,
					collectionLinks,
					categoryClassString,
					sidebarQueriesHeading: trendingHeading,
					relatedProductsHeading: trendingProductsHeading,
					liveRegionText,
				});

				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				this.initProductsSlider();
				this.calcHeight();
			})
			.catch(() => {})
			.finally(() => {
				this._searchspringInitialPending = false;
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		if (!this.statusElement) return;
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement?.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		// Destroy recommended slider before injecting results to prevent event conflicts
		this.destroyRecommendedSlider();

		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}

	initProductsSlider() {
		if (typeof Swiper === "undefined") return;

		// Destroy previous instance to avoid duplicates on re-render
		if (this._productsSwiper) {
			this._productsSwiper.destroy(true, true);
			this._productsSwiper = null;
		}

		const slider = this.querySelector(".predictive-search-products-slider.swiper");
		if (!slider) return;

		const container = slider.closest(".predictive-search-slider-container");
		const nextBtn = container?.querySelector(".predictive-slider-next");

		// Ensure the list is visible before Swiper calculates widths
		const list = slider.querySelector(".predictive-search__results-list_products");
		if (list) list.classList.add("active");

		const syncNextNav = (swiper) =>
			updatePredictiveProductsNextNav(swiper, nextBtn);

		this._productsSwiper = new Swiper(slider, {
			slidesPerView: "auto",
			spaceBetween: 20,
			watchOverflow: true,
			observer: true,
			observeParents: true,
			resizeObserver: true,
			updateOnWindowResize: true,
			navigation: container ? {
				nextEl: nextBtn,
				prevEl: container.querySelector(".predictive-slider-prev"),
			} : false,
			on: {
				init(swiper) {
					requestAnimationFrame(() => {
						swiper.update();
						syncNextNav(swiper);
					});
				},
				slideChange(swiper) {
					syncNextNav(swiper);
				},
				resize(swiper) {
					swiper.update();
					syncNextNav(swiper);
				},
			},
		});
	}

	destroyRecommendedSlider() {
		const slider = this.querySelector(".predictive-search-recommended-slider.swiper");
		if (slider && slider.swiper) {
			slider.swiper.destroy(true, true);
		}
	}

	initRecommendedSlider() {
		if (typeof Swiper === "undefined") return;

		const slider = this.querySelector(".predictive-search-recommended-slider.swiper");
		if (!slider || slider.classList.contains("swiper-initialized")) return;

		const container = slider.closest(".predictive-search-slider-container");

		new Swiper(slider, {
			slidesPerView: "auto",
			spaceBetween: 20,
			watchOverflow: true,
			observer: true,
			observeParents: true,
			resizeObserver: true,
			updateOnWindowResize: true,
			navigation: container ? {
				nextEl: container.querySelector(".predictive-slider-next"),
				prevEl: container.querySelector(".predictive-slider-prev"),
			} : false,
			on: {
				init(swiper) {
					requestAnimationFrame(() => swiper.update());
				},
			},
		});
	}

	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		if (results.length != 0) {
			results[0].classList.add("active");
			this.querySelector(".predictive-search__result-tab").classList.add(
				"active",
			);
			this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					const typeTarget = tab.dataset.typeTarget;
					this.querySelectorAll(".predictive-search__result-tab").forEach(
						(element) => {
							element.classList.remove("active");
						},
					);
					tab.classList.add("active");

					results.forEach((element) => {
						let resultsType = element.dataset.type;
						if (resultsType == typeTarget) {
							element.classList.add("active");
						} else {
							element.classList.remove("active");
						}
					});
				});
			});
		}
	}

	hidePromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		}
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
		// Re-init recommended slider when returning to initial state
		this.initRecommendedSlider();
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		const live = this.querySelector(
			"[data-predictive-search-live-region-count-value]",
		);
		if (live) this.setLiveRegionText(live.textContent);
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.predictiveSearchResults.removeAttribute("style");
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
